import React, { useCallback, useState } from 'react';
import { Block } from 'baseui/block';
import { useStyletron } from 'baseui';
import { Modal } from '../modal/Modal';
import { useClientInfo } from '../../context/ClientInfoContext';
import {
  ActionsModal,
  isLanguagesResult,
  isReasonsResult,
} from '../actions-modal/ActionsModal';
import {
  AgentStatusT,
  SupportedLanguage,
  TwoWayService,
} from '../relay-messenger-axios-service/relay-messenger-axios-service';
import { useCustomerInfo } from '../../context/CustomerInfoContext';
import { CustomerActionsPopover } from '../customer-actions/CustomerActionsPopover';
import { useUserInfo } from '../../context/UserInfoContext';
import { useAlertMessage } from '../../hooks/useAlertMessage/use-alert-message';
import {
  ClosedReasons,
  ClosedReasonsService,
} from '../../../setup/components/setup-conversations/components/closed-reasons-axios-service/closed-reasons-axios-service';
import { useRefreshConversations } from '../../context/RefreshConversationsContext';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';
import { useNavigate } from 'react-router-dom';

export type TwoWayMessagingCustomerInfoProps = {
  conversationsClicked: {
    isMyConversationsClicked: boolean;
    IsMyUnassignedConversationClicked: boolean;
    IsMyClosedConversationsClicked: boolean;
  };
  setAgentStatus: (agentStatus: AgentStatusT) => void;
  customerCcid?: string;
  setIsPingTriggered: (triggerFlag: boolean) => void;
  // lets async handlers (openPingModal, fetchClosedReasons) detect if the agent
  // switched conversations while their requests were still in flight, so they
  // can discard stale results instead of acting on the wrong channel
  selectedChannelRef?: React.RefObject<string | undefined>;
};

export const TwoWayMessagingCustomerInfo = ({
  conversationsClicked: { isMyConversationsClicked },
  setAgentStatus,
  customerCcid,
  setIsPingTriggered,
  selectedChannelRef,
}: TwoWayMessagingCustomerInfoProps) => {
  const [css] = useStyletron();
  const [{ clientId, rmTranslationsEnabled: isRMTranslationsEnabled }] =
    useClientInfo();
  const {
    customerInfo: { firstName, lastName, productGroupId, channelSid },
    setCustomerInfo,
  } = useCustomerInfo();
  const [{ userId }] = useUserInfo();
  const { setRefreshConversations } = useRefreshConversations();
  const featureFlags = useFeatureFlags();
  const isRMPingTranslationsEnabled =
    featureFlags.find((flag) => flag.flagName === 'rm_ping_translations')
      ?.flagValue ?? false;
  const [isActionsPopoverOpen, setIsActionsPopoverOpen] = useState(false);
  const [isPingModalOpen, setIsPingModalOpen] = useState(false);
  const [isCloseChannelModalOpen, setIsCloseChannelModalOpen] = useState(false);
  const [closedReasons, setClosedReasons] = useState<ClosedReasons[]>([]);
  const [pingLanguages, setPingLanguages] = useState<SupportedLanguage[]>([]);
  const [isErrorPingLanguages, setIsErrorPingLanguages] = useState(false);
  const [showPingLanguageSelector, setShowPingLanguageSelector] =
    useState(false);
  const showMessage = useAlertMessage();
  const navigate = useNavigate();

  const fetchClosedReasons = useCallback(async () => {
    const initiatingChannelSid = channelSid;
    try {
      const data = await ClosedReasonsService.getClient(clientId);
      if (
        data &&
        data.client &&
        data.client.two_way_config &&
        data.client.two_way_config.close_reasons_enabled
      ) {
        const closedReasonsRes = await ClosedReasonsService.getClosedReasons(
          clientId,
        );
        setClosedReasons(closedReasonsRes);
      } else {
        setClosedReasons([]);
      }
      if (
        selectedChannelRef &&
        selectedChannelRef.current !== initiatingChannelSid
      ) {
        // the agent switched conversations while closed reasons were loading;
        // don't open a close-conversation dialog for the wrong channel
        return;
      }
      setIsCloseChannelModalOpen(true);
    } catch (error) {
      console.error('Error fetching closed reasons', error);
    }
  }, [
    clientId,
    channelSid,
    selectedChannelRef,
    setIsCloseChannelModalOpen,
    setClosedReasons,
  ]);

  const fetchPingLanguages = useCallback(async () => {
    if (!channelSid) {
      return;
    }
    try {
      const result = await TwoWayService.getPingMessageSupportedLanguages(
        clientId,
        channelSid,
      );
      if (result && result.length > 0) {
        setPingLanguages(result);
      } else {
        // getPingMessageSupportedLanguages should always return at least one language,
        // but if it doesn't, default to English
        setPingLanguages([{ code: 'en', name: 'English' }]);
      }
      setIsErrorPingLanguages(false);
    } catch (error) {
      setIsErrorPingLanguages(true);
      // English always works as a ping language, so agents can still ping
      // even when the supported-languages list fails to load
      setPingLanguages([{ code: 'en', name: 'English' }]);
    }
  }, [clientId, channelSid]);

  const openPingModal = useCallback(async () => {
    const initiatingChannelSid = channelSid;
    const translationsEnabledForPing =
      isRMTranslationsEnabled && isRMPingTranslationsEnabled;
    if (translationsEnabledForPing && channelSid) {
      await fetchPingLanguages();
    }
    if (
      selectedChannelRef &&
      selectedChannelRef.current !== initiatingChannelSid
    ) {
      // the agent switched conversations while languages were loading;
      // discard this stale result instead of opening a ping modal for the wrong channel
      return;
    }
    setShowPingLanguageSelector(translationsEnabledForPing);
    setIsPingModalOpen(true);
  }, [
    isRMTranslationsEnabled,
    isRMPingTranslationsEnabled,
    channelSid,
    fetchPingLanguages,
    selectedChannelRef,
  ]);

  // reset ping-selector state on close so it can't leak stale error/language
  // data into a future open that doesn't happen to go through openPingModal
  const dismissPingModal = useCallback(() => {
    setIsPingModalOpen(false);
    setIsErrorPingLanguages(false);
    setShowPingLanguageSelector(false);
    setPingLanguages([]);
  }, []);

  // reset closed-reasons state on close so it can't leak stale options into
  // a future open that doesn't happen to go through fetchClosedReasons
  const dismissCloseChannelModal = useCallback(() => {
    setIsCloseChannelModalOpen(false);
    setClosedReasons([]);
  }, []);

  const pingCustomer = async (language?: string) => {
    dismissPingModal();
    if (!channelSid) {
      showMessage('Cannot send ping.', 'error');
      return;
    }
    try {
      await TwoWayService.setAgentStatus(clientId, userId, 'ping');
      // Call new ping route with a language parameter if translations are enabled for a client
      await TwoWayService.pingUser(clientId, channelSid, language);
      showMessage('Ping sent to customer!', 'success');
      setIsPingTriggered(true);
      setAgentStatus('active');
    } catch (error) {
      const errCode = error.response?.data.code;
      if (errCode === 'PING_SUPPRESSION') {
        showMessage('Customer was already pinged recently', 'error');
      } else if (errCode === 'BAD_CONSENT') {
        showMessage(
          'Customer has not given consent to receive notifications',
          'error',
        );
      } else {
        showMessage('There was an error sending the ping', 'error');
      }
    }
  };

  const navigateToExperienceLauncher = () => {
    document.location.href = `/launcher/launchforuser/${customerCcid}?chat=${channelSid}`;
  };

  const closeConversation = async (reasonId, reasonValue, reasonText) => {
    dismissCloseChannelModal();
    try {
      await TwoWayService.closeConversation(
        clientId,
        channelSid,
        reasonId,
        reasonValue,
        reasonText,
      );
      await TwoWayService.setAgentStatus(clientId, userId, 'close');
      setAgentStatus('active');
      setRefreshConversations(true); // refresh conversations list
      setCustomerInfo({}); // hide customer info
      navigate(`/${clientId}/assigned`, { replace: true }); // navigate to assigned conversations
    } catch (error) {
      const errMsg = `There was an issue closing the conversation: ${error.response.data}`;
      showMessage(errMsg, 'error');
    }
  };

  return (
    <Block
      overrides={{
        Block: {
          style: () => ({
            marginTop: '20px',
            marginLeft: '20px',
            marginRight: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '20px',
            height: '62px',
          }),
        },
      }}
    >
      <div data-testid="customer-information">
        <>
          <p
            className={css({
              fontWeight: '700',
              lineHeight: '14px',
              marginBottom: '0',
            })}
            data-testid="customer-name"
          >
            {firstName} {lastName}
          </p>
          <p className={css({ marginBottom: '0' })} data-testid="customer-ccid">
            Account ID (CCID): {customerCcid}
          </p>
          <p
            className={css({ marginBottom: '0' })}
            data-testid="customer-product-group-id"
          >
            Product Group: {productGroupId}
          </p>
        </>
      </div>
      <div>
        {isMyConversationsClicked && (
          <CustomerActionsPopover
            isActionsPopoverOpen={isActionsPopoverOpen}
            setIsActionsPopoverOpen={setIsActionsPopoverOpen}
            setIsPingModalOpen={openPingModal}
            setIsCloseChannelModalOpen={fetchClosedReasons}
            navigateToExperienceLauncher={navigateToExperienceLauncher}
          />
        )}
      </div>
      {isPingModalOpen && (
        <Modal
          closeFn={dismissPingModal}
          isOpen={isPingModalOpen}
          autoFocus={false}
          content={
            <ActionsModal
              closeModal={dismissPingModal}
              successModal={(result) =>
                pingCustomer(
                  isLanguagesResult(result) ? result.language : undefined,
                )
              }
              modalSubTitle={
                <>
                  Are you sure you want to <strong>send a ping</strong> to this
                  customer?
                </>
              }
              cancelBtnText="No, go back"
              successBtnText="Yes"
              dataTestId="confirm-ping-customer-btn"
              selector={
                showPingLanguageSelector
                  ? { kind: 'languages', options: pingLanguages }
                  : undefined
              }
              selectorErrorMsg={
                isErrorPingLanguages ? 'Failed to load languages.' : undefined
              }
              onSelectorRetry={fetchPingLanguages}
              onSelectorErrorDismiss={() => setIsErrorPingLanguages(false)}
              selectLabel="Select Language"
            />
          }
          modalTitle="Ping Customer?"
          closeable={false}
        />
      )}
      {isCloseChannelModalOpen && (
        <Modal
          closeFn={dismissCloseChannelModal}
          isOpen={isCloseChannelModalOpen}
          autoFocus={false}
          content={
            <ActionsModal
              closeModal={dismissCloseChannelModal}
              successModal={(result) => {
                if (isReasonsResult(result)) {
                  closeConversation(
                    result.reasonId,
                    result.reasonName,
                    result.reasonText,
                  );
                }
              }}
              modalSubTitle="Are you sure you want to close this conversation? The conversation would be permanently closed and would no longer be available, unless re-opened by the customer."
              cancelBtnText="Cancel"
              successBtnText="Yes"
              dataTestId="confirm-close-conversation-btn"
              selector={{ kind: 'reasons', options: closedReasons }}
              selectLabel="Select Close Conversation Reason"
            />
          }
          modalTitle="Close Conversation?"
          closeable={false}
        />
      )}
    </Block>
  );
};
